import React, {useMemo, useRef, useState} from 'react'
import {useRouter} from 'next/router'
import {shallow} from 'zustand/shallow'

import classNames from 'classnames'

import {useLoginStore} from '@store/LoginStore'
import useDidMountEffect from '@hook/UseDidMountEffect'
import useNavigation from '@hook/useNavigation'
import useIntersectionObserver from '@hook/useIntersectionObserver'

import {createLog} from '@util/logs'

import NavigationTitleOperations from '@component/header/GlobalNavigation/NavigationOperations/NavigationTitleOperations'
import NavigationLoginOperation from '@component/header/GlobalNavigation/NavigationOperations/NavigationLoginOperation'
import NavigationOperations from '@component/header/GlobalNavigation/NavigationOperations/NavigationOperations'
import NavigationMyOperations from '@component/header/GlobalNavigation/NavigationOperations/NavigationMyOperations'
import MobileNavTitle from '@component/header/GlobalNavigation/MobileNavTitle/MobileNavTitle'
import NavigationAccountOperation from '@component/header/GlobalNavigation/NavigationOperations/NavigationAccountOperation'
import MobileAccountNavigation from '@component/header/GlobalNavigation/MobileAccountNavigation/MobileAccountNavigation'
import NavTitle from '@component/header/GlobalNavigation/NavTitle/NavTitle'

type IProps = {
    className?: string
    enableFloatingNavigation?: boolean
    hideLoginButtons?: boolean
    navTheme?: 'light' | 'dark'
    navLogoType?: 'default' | 'white'
    scrolledNavColor?: string
    dataCy?: string
}

export interface IGlobalNavigationStyles {
    logoType?: 'default' | 'white'
    TextPrimaryColor?: string
    TextSecondaryColor?: string
    iconColor?: string
    buttonPrimaryBackground?: string
    buttonPrimaryText?: string
}

const GlobalNavigation: React.FC<IProps> = ({
    enableFloatingNavigation = false,
    className,
    hideLoginButtons = false,
    navTheme = 'light',
    navLogoType,
    scrolledNavColor,
    dataCy = 'global_nav',
}) => {
    const {goLeaderboard} = useNavigation()

    const router = useRouter()

    const {id, accessToken} = useLoginStore(
        state => ({
            id: state.id,
            accessToken: state.accessToken,
        }),
        shallow,
    )

    const {observer: isScrolled, ref: navRef} = useIntersectionObserver()

    useDidMountEffect(() => {
        if (router?.pathname?.includes('/user') || router?.pathname?.includes('/key')) {
            goLeaderboard('main')
        }
    }, [id])

    const mobileAccountNavigationRef = useRef<any>()
    const [notificationTrigger, setNotificationTrigger] = useState(false)

    const isLogin = useMemo(() => id && id.length > 0 && accessToken && accessToken.length > 0, [id, accessToken])

    const navStyles = useMemo(() => {
        const navStyleLight: IGlobalNavigationStyles = {
            logoType: navLogoType || 'default',
            TextPrimaryColor: 'text-gray01 dark:text-dark_gray01',
            TextSecondaryColor: 'text-gray02 dark:text-dark_gray02',
            iconColor: 'fill-gray01 dark:fill-dark_gray01',
        }
        const navStyleDark: IGlobalNavigationStyles = {
            logoType: navLogoType || 'white',
            TextPrimaryColor: 'text-white',
            TextSecondaryColor: 'text-dark_gray02',
            iconColor: 'fill-dark_gray01 sm:fill-dark_gray01',
        }

        if (!enableFloatingNavigation) {
            return navStyleLight
        } else {
            switch (navTheme) {
                case 'light':
                    return navStyleLight
                case 'dark':
                    if (!isScrolled) {
                        return navStyleDark
                    } else {
                        return navStyleLight
                    }
            }
        }
    }, [isScrolled, enableFloatingNavigation, navTheme, navLogoType])

    const onClickMobileAccountNavigation = () => {
        createLog('event', 'navigation_open_mobile_account_navigation')
        mobileAccountNavigationRef?.current?.show()
    }

    return (
        <>
            <header
                className={classNames(
                    'px-[20px] md:px-[15px] flex min-w-screen items-center justify-between py-[11px] md:py-[18px]',
                    'z-sticky',
                    enableFloatingNavigation
                        ? isScrolled
                            ? `bg-white top-0 dark:bg-bg_dark_gnb ${scrolledNavColor}`
                            : 'bg-transparent absolute'
                        : 'bg-white dark:bg-bg_dark_gnb',
                    !isScrolled && enableFloatingNavigation ? '' : 'shadow-normal',
                    className,
                )}
                data-cy={dataCy}>
                {/* title */}
                <NavTitle
                    className={'md:flex-1 sm:hidden sm:flex-auto'}
                    overlayWhite={!isScrolled && enableFloatingNavigation}
                    logoType={navStyles?.logoType}
                />
                <MobileNavTitle className={'hidden sm:block sm:flex-1'} />

                {/* leaderboard, portfolio, token, community, features ... */}
                <NavigationTitleOperations navStyles={navStyles} isScrolled={isScrolled} />

                {/* sign in and register */}
                {!isLogin && hideLoginButtons === false && (
                    <NavigationLoginOperation className={'mr-[10px]'} navStyles={navStyles} isScrolled={isScrolled} />
                )}
                {/* point, account */}
                {isLogin && (
                    <NavigationAccountOperation
                        className={'mr-[10px] md:mr-[0px]'}
                        navStyles={navStyles}
                        onClickMobileAccountNavigation={onClickMobileAccountNavigation}
                    />
                )}
                <div className={`flex items-center gap-x-[10px]`}>
                    {/* notification, invoice ...  */}
                    {isLogin && (
                        <NavigationMyOperations
                            navStyles={navStyles}
                            notificationTrigger={notificationTrigger}
                            setNotificationTrigger={setNotificationTrigger}
                        />
                    )}

                    {/* search, language, currency, dark mode ... */}
                    <NavigationOperations navStyles={navStyles} />
                </div>
                <MobileAccountNavigation
                    ref={mobileAccountNavigationRef}
                    onClickNotification={() => {
                        setNotificationTrigger(!notificationTrigger)
                    }}
                />
            </header>
            {/* scroll indicator */}
            <div ref={navRef} className={`absolute top-0 w-full h-[50px] md:h-[60px] invisible`} />
        </>
    )
}

export default React.memo(GlobalNavigation)
